import { doc, setDoc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';

// ==================== TIPOS ====================

export interface OnboardingData {
  // Passo 1: Concurso
  targetExam: string;
  selectedExamId?: string; // ID do edital real no Firestore
  selectedCargo?: string;
  uploadId?: string;
  extractedSubjects?: { name: string; topics: string[]; confidence?: string }[];
  targetBanca: string;
  targetArea: string;
  
  // Passo 2: Capacidade de Estudo
  hoursPerDay: number;
  daysPerWeek: number;
  
  // Passo 3: Nível de Base (opcional)
  baseLevel: {
    portugues: 'fraco' | 'medio' | 'forte';
    raciocinio: 'fraco' | 'medio' | 'forte';
    direito: 'fraco' | 'medio' | 'forte';
  };
  baseLevelSkipped: boolean;
  
  // Passo 4: Maturidade (opcional)
  studyTime: string;
  majorDifficulty: string;
  maturitySkipped: boolean;
  
  // Passo 5: Prazo
  targetDate: string;
  hasNoDate: boolean;
}

export interface UserOnboarding {
  completed: boolean;
  completedAt?: any;
  currentStep: number;
  data: OnboardingData;
  updatedAt?: any;
}

export interface StudyPlan {
  id: string;
  createdAt: any;
  updatedAt: any;
  targetExam: string;
  totalWeeks: number;
  totalHours: number;
  hoursPerWeek: number;
  subjects: SubjectPlan[];
  paretoTopics: string[];
  weeklySchedule: WeekSchedule[];
  tips: string[];
  startDate?: string; // YYYY-MM-DD (segunda da geração)
  onboardingSnapshot?: {
    targetDate?: string | null;
    hasNoDate: boolean;
    daysPerWeek: number;
    hoursPerDay: number;
    selectedExamId?: string | null;
    uploadId?: string | null;
    planSource?: 'extractedSubjects' | 'examId' | 'fallback';
    extractedSubjectsCount?: number;
    extractedSubjectsFingerprint?: string;
    generatedAt?: string; // ISO
  };
}

export interface SubjectPlan {
  name: string;
  priority: 'alta' | 'media' | 'baixa';
  hoursPerWeek: number;
  topics: string[];
}

export interface WeekSchedule {
  weekNumber: number;
  title: string;
  days: DaySchedule[];
}

export interface DaySchedule {
  dayName: string;
  hours: number;
  subject: string;
  topic: string;
}

export interface UserEntitlements {
  plan: 'free' | 'essencial' | 'premium';
  dailyQuestionsLimit: number;
  dailyQuestionsUsed: number;
  aiExplanationLimit: number;
  aiExplanationsUsed: number;
  lastResetDate: string;
}

// ==================== FUNÇÕES DE ONBOARDING ====================

/**
 * Salva o progresso do onboarding incrementalmente (a cada passo)
 */
const sanitizeForFirestore = (value: any): any => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeForFirestore(item))
      .filter((item) => item !== undefined);
  }
  if (typeof value === 'object') {
    const cleaned: any = {};
    Object.entries(value).forEach(([k, v]) => {
      const sv = sanitizeForFirestore(v);
      if (sv !== undefined) cleaned[k] = sv;
    });
    return cleaned;
  }
  return value;
};

const sanitizePartial = (data: Partial<OnboardingData>) => sanitizeForFirestore(data) || {};

export const saveOnboardingProgress = async (
  step: number, 
  data: Partial<OnboardingData>
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);
  const cleanData = sanitizePartial(data);

  if (userDoc.exists()) {
    // Atualiza o documento existente
    await updateDoc(userRef, {
      'onboarding.currentStep': step,
      'onboarding.data': cleanData,
      'onboarding.updatedAt': serverTimestamp()
    });
  } else {
    // Cria o documento inicial
    await setDoc(userRef, {
      email: user.email,
      createdAt: serverTimestamp(),
      onboarding: {
        completed: false,
        currentStep: step,
        data: cleanData,
        updatedAt: serverTimestamp()
      },
      entitlements: getDefaultEntitlements()
    });
  }
};

/**
 * Recupera o progresso do onboarding (para retomar de onde parou)
 */
export const getOnboardingProgress = async (): Promise<UserOnboarding | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists() && userDoc.data().onboarding) {
    return userDoc.data().onboarding as UserOnboarding;
  }

  return null;
};

/**
 * Marca o onboarding como completo e salva os dados finais
 */
export const completeOnboarding = async (data: OnboardingData): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');

  const userRef = doc(db, 'users', user.uid);
  const cleanData = sanitizeForFirestore(data);

  // setDoc + merge evita erro quando documento do usuário ainda não existe
  await setDoc(userRef, {
    email: user.email || null,
    onboarding: {
      completed: true,
      completedAt: serverTimestamp(),
      data: cleanData,
      updatedAt: serverTimestamp(),
    },
    profile: {
      targetExam: cleanData.targetExam,
      targetBanca: cleanData.targetBanca,
      targetArea: cleanData.targetArea,
      hoursPerDay: cleanData.hoursPerDay,
      daysPerWeek: cleanData.daysPerWeek,
    },
    entitlements: getDefaultEntitlements(),
  }, { merge: true });
};

/**
 * Verifica se o usuário já completou o onboarding
 */
export const hasCompletedOnboarding = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists() && userDoc.data().onboarding) {
    return userDoc.data().onboarding.completed === true;
  }

  return false;
};

/**
 * Reseta o onboarding para permitir refazer o plano de estudos
 * NÃO apaga o progresso de questões respondidas
 */
export const resetOnboarding = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');

  const userRef = doc(db, 'users', user.uid);
  
  await updateDoc(userRef, {
    'onboarding.completed': false,
    'onboarding.currentStep': 0,
    'onboarding.data': null,
    'onboarding.updatedAt': serverTimestamp(),
    'studyPlan': null
  });
};

// ==================== FUNÇÕES DE PLANO DE ESTUDOS ====================

/**
 * Salva o plano de estudos gerado pela IA
 */
export const saveStudyPlan = async (plan: Omit<StudyPlan, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');

  const planId = `plan_${Date.now()}`;
  const userRef = doc(db, 'users', user.uid);

  await updateDoc(userRef, {
    studyPlan: {
      ...plan,
      id: planId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
  });

  return planId;
};

/**
 * Recupera o plano de estudos do usuário
 */
export const getStudyPlan = async (): Promise<StudyPlan | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists() && userDoc.data().studyPlan) {
    return userDoc.data().studyPlan as StudyPlan;
  }

  return null;
};

/**
 * Alterna o status de conclusão de um dia no plano de estudos
 */
export const toggleDayCompletion = async (weekNumber: number, dayIndex: number): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists() || !userDoc.data().studyPlan) return;

  const studyPlan = userDoc.data().studyPlan as StudyPlan;
  const weeklySchedule = studyPlan.weeklySchedule || [];

  // Encontra a semana e o dia
  const week = weeklySchedule.find(w => w.weekNumber === weekNumber);
  if (!week || !week.days[dayIndex]) return;

  // Alterna o status
  const currentStatus = week.days[dayIndex].completed || false;
  week.days[dayIndex].completed = !currentStatus;

  // Atualiza no Firestore
  await updateDoc(userRef, {
    'studyPlan.weeklySchedule': weeklySchedule,
    'studyPlan.updatedAt': serverTimestamp()
  });
};

// ==================== FUNÇÕES DE ENTITLEMENTS ====================

/**
 * Retorna os entitlements padrão para o plano gratuito
 */
export const getDefaultEntitlements = (): UserEntitlements => ({
  plan: 'free',
  dailyQuestionsLimit: 10,
  dailyQuestionsUsed: 0,
  aiExplanationLimit: 5,
  aiExplanationsUsed: 0,
  lastResetDate: new Date().toISOString().split('T')[0]
});

/**
 * Recupera os entitlements do usuário
 */
export const getUserEntitlements = async (): Promise<UserEntitlements> => {
  const user = auth.currentUser;
  if (!user) return getDefaultEntitlements();

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists() && userDoc.data().entitlements) {
    const entitlements = userDoc.data().entitlements as UserEntitlements;
    const normalized: UserEntitlements = {
      ...getDefaultEntitlements(),
      ...entitlements
    };

    // Força limites corretos para o plano free
    if (normalized.plan === 'free') {
      normalized.dailyQuestionsLimit = 10;
      normalized.aiExplanationLimit = 5;
    }

    // Verifica se precisa resetar os contadores diários
    const today = new Date().toISOString().split('T')[0];
    if (normalized.lastResetDate !== today) {
      const resetEntitlements = {
        ...normalized,
        dailyQuestionsUsed: 0,
        aiExplanationsUsed: 0,
        lastResetDate: today
      };
      await updateDoc(userRef, { entitlements: resetEntitlements });
      return resetEntitlements;
    }
    
    return normalized;
  }

  // Cria entitlements padrão se não existir
  const defaultEntitlements = getDefaultEntitlements();
  await updateDoc(userRef, { entitlements: defaultEntitlements });
  return defaultEntitlements;
};

/**
 * Incrementa o contador de questões usadas
 */
export const incrementQuestionsUsed = async (): Promise<{ canContinue: boolean; remaining: number }> => {
  const user = auth.currentUser;
  if (!user) return { canContinue: false, remaining: 0 };

  const entitlements = await getUserEntitlements();
  const newUsed = entitlements.dailyQuestionsUsed + 1;
  const remaining = entitlements.dailyQuestionsLimit - newUsed;

  const userRef = doc(db, 'users', user.uid);
  await updateDoc(userRef, {
    'entitlements.dailyQuestionsUsed': newUsed
  });

  return {
    canContinue: remaining > 0,
    remaining: Math.max(0, remaining)
  };
};

/**
 * Incrementa o contador de explicações da IA usadas
 */
export const incrementAIExplanationsUsed = async (): Promise<{ canContinue: boolean; remaining: number }> => {
  const user = auth.currentUser;
  if (!user) return { canContinue: false, remaining: 0 };

  const entitlements = await getUserEntitlements();
  const newUsed = entitlements.aiExplanationsUsed + 1;
  const remaining = entitlements.aiExplanationLimit - newUsed;

  const userRef = doc(db, 'users', user.uid);
  await updateDoc(userRef, {
    'entitlements.aiExplanationsUsed': newUsed
  });

  return {
    canContinue: remaining > 0,
    remaining: Math.max(0, remaining)
  };
};

/**
 * Atualiza o plano do usuário (upgrade)
 */
export const upgradePlan = async (newPlan: 'essencial' | 'premium'): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');

  const limits = {
    essencial: { dailyQuestionsLimit: 50, aiExplanationLimit: 30 },
    premium: { dailyQuestionsLimit: 999, aiExplanationLimit: 999 }
  };

  const userRef = doc(db, 'users', user.uid);
  await updateDoc(userRef, {
    'entitlements.plan': newPlan,
    'entitlements.dailyQuestionsLimit': limits[newPlan].dailyQuestionsLimit,
    'entitlements.aiExplanationLimit': limits[newPlan].aiExplanationLimit
  });
};

// ==================== FUNÇÕES AUXILIARES ====================

/**
 * Calcula o total de horas até a data-alvo
 */
export const calculateTotalHours = (data: OnboardingData): number => {
  const weeklyHours = data.hoursPerDay * data.daysPerWeek;
  
  if (data.hasNoDate) {
    // Plano base de 12 semanas
    return weeklyHours * 12;
  }
  
  const targetDate = new Date(data.targetDate);
  const today = new Date();
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const weeks = diffDays / 7;
  
  return Math.round(weeks * weeklyHours);
};

/**
 * Calcula o número de semanas até a data-alvo
 */
export const calculateWeeksUntilTarget = (data: OnboardingData): number => {
  if (data.hasNoDate) {
    return 12; // Plano base
  }
  
  const targetDate = new Date(data.targetDate);
  const today = new Date();
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.ceil(diffDays / 7);
};

// ==================== FUNÇÕES DE ADMINISTRAÇÃO ====================

/**
 * Busca todos os usuários que têm plano de estudos baseado em um edital específico
 * REQUER: Regras do Firestore permitindo leitura da coleção 'users' para admins
 */
export const getUsersByExamId = async (examId: string): Promise<{ id: string; email: string; planCreatedAt: any }[]> => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const affectedUsers: { id: string; email: string; planCreatedAt: any }[] = [];
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const selectedExamId = data.onboarding?.data?.selectedExamId;
      
      if (selectedExamId === examId && data.studyPlan) {
        affectedUsers.push({
          id: doc.id,
          email: data.email || 'N/A',
          planCreatedAt: data.studyPlan.createdAt
        });
      }
    });
    
    return affectedUsers;
  } catch (error: any) {
    console.error('Erro ao buscar usuários:', error);
    
    // Se for erro de permissão, dá uma mensagem mais clara
    if (error.code === 'permission-denied') {
      throw new Error('Permissão negada. Atualize as regras do Firestore para permitir leitura de admins na coleção "users".');
    }
    
    throw new Error('Erro ao buscar usuários: ' + error.message);
  }
};

/**
 * Regenera o plano de estudos de um usuário específico com base no edital atualizado
 */
export const regenerateUserStudyPlan = async (
  userId: string, 
  examSubjects: { name: string; topics: string[] }[],
  examMetadata: { institution: string; role: string; banca: string }
): Promise<void> => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    throw new Error('Usuário não encontrado');
  }
  
  const userData = userDoc.data();
  const onboardingData = userData.onboarding?.data as OnboardingData;
  
  if (!onboardingData) {
    throw new Error('Dados de onboarding não encontrados');
  }
  
  // Gera novo plano baseado no edital atualizado
  const newPlan = generateStudyPlanFromExam(onboardingData, examSubjects, examMetadata);
  
  // Atualiza o plano no Firestore
  await updateDoc(userRef, {
    studyPlan: {
      ...newPlan,
      id: `plan_${Date.now()}`,
      createdAt: userData.studyPlan?.createdAt || serverTimestamp(), // Mantém a data original
      updatedAt: serverTimestamp(),
      regeneratedFromExam: true,
      regeneratedAt: serverTimestamp()
    }
  });
};

/**
 * Gera um plano de estudos a partir dos dados do edital
 */
const generateStudyPlanFromExam = (
  onboardingData: OnboardingData,
  examSubjects: { name: string; topics: string[] }[],
  examMetadata: { institution: string; role: string; banca: string }
): Omit<StudyPlan, 'id' | 'createdAt' | 'updatedAt'> => {
  const hoursPerWeek = onboardingData.hoursPerDay * onboardingData.daysPerWeek;
  const totalWeeks = calculateWeeksUntilTarget(onboardingData);
  const totalHours = hoursPerWeek * totalWeeks;
  
  // Distribui horas entre as matérias
  const subjectCount = examSubjects.length || 1;
  const hoursPerSubject = hoursPerWeek / subjectCount;
  
  // Define prioridades baseado no nível do usuário
  const subjects: SubjectPlan[] = examSubjects.map((subject, index) => {
    let priority: 'alta' | 'media' | 'baixa' = 'media';
    
    // Primeira metade das matérias = alta prioridade (geralmente mais importantes)
    if (index < subjectCount / 3) priority = 'alta';
    else if (index < (subjectCount * 2) / 3) priority = 'media';
    else priority = 'baixa';
    
    return {
      name: subject.name,
      priority,
      hoursPerWeek: Math.round(hoursPerSubject * 10) / 10,
      topics: subject.topics
    };
  });
  
  // Gera cronograma semanal simplificado (começando HOJE e seguindo em frente)
  const weeklySchedule: WeekSchedule[] = [];
  const daysOfWeek = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
  const todayIdx = new Date().getDay(); // 0=Domingo
  const rotationStart = todayIdx === 0 ? 6 : todayIdx - 1; // 0->Domingo vira 6, Segunda=1 vira 0
  
  for (let week = 1; week <= Math.min(totalWeeks, 12); week++) {
    const days: DaySchedule[] = [];
    
    for (let d = 0; d < onboardingData.daysPerWeek; d++) {
      const globalOffset = (week - 1) * onboardingData.daysPerWeek + d;
      const dayName = daysOfWeek[(rotationStart + globalOffset) % 7];
      const subjectIndex = globalOffset % subjectCount;
      const subject = subjects[subjectIndex] || subjects[0];
      const topicIndex = Math.floor(globalOffset / 2) % (subject?.topics?.length || 1);
      
      days.push({
        dayName,
        hours: onboardingData.hoursPerDay,
        subject: subject?.name || 'Estudo Geral',
        topic: subject?.topics?.[topicIndex] || 'Revisão geral'
      });
    }
    
    weeklySchedule.push({
      weekNumber: week,
      title: `Semana ${week}`,
      days
    });
  }
  
  // Tópicos mais importantes (Pareto)
  const paretoTopics = subjects
    .filter(s => s.priority === 'alta')
    .flatMap(s => s.topics.slice(0, 3))
    .slice(0, 10);
  
  const startDate = new Date().toISOString().split('T')[0];

  return {
    targetExam: `${examMetadata.institution} - ${examMetadata.role}`,
    totalWeeks,
    totalHours,
    hoursPerWeek,
    subjects,
    paretoTopics,
    weeklySchedule,
    startDate,
    onboardingSnapshot: {
      targetDate: onboardingData.targetDate || null,
      hasNoDate: onboardingData.hasNoDate,
      daysPerWeek: onboardingData.daysPerWeek,
      hoursPerDay: onboardingData.hoursPerDay,
      selectedExamId: onboardingData.selectedExamId || null,
      generatedAt: new Date().toISOString(),
      startDate,
    },
    tips: [
      `Foque nas matérias de alta prioridade primeiro`,
      `A banca ${examMetadata.banca} costuma cobrar muito a letra da lei`,
      `Faça pelo menos 20 questões por dia da matéria do dia`,
      `Revise os erros antes de dormir`
    ]
  };
};

/**
 * Regenera os planos de todos os usuários baseados em um edital
 * Retorna estatísticas da operação
 */
export const batchRegenerateStudyPlans = async (
  examId: string,
  examSubjects: { name: string; topics: string[] }[],
  examMetadata: { institution: string; role: string; banca: string },
  onProgress?: (current: number, total: number, email: string) => void
): Promise<{ success: number; errors: number; total: number }> => {
  const affectedUsers = await getUsersByExamId(examId);
  
  let success = 0;
  let errors = 0;
  
  for (let i = 0; i < affectedUsers.length; i++) {
    const user = affectedUsers[i];
    
    if (onProgress) {
      onProgress(i + 1, affectedUsers.length, user.email);
    }
    
    try {
      await regenerateUserStudyPlan(user.id, examSubjects, examMetadata);
      success++;
    } catch (error) {
      console.error(`Erro ao regenerar plano do usuário ${user.id}:`, error);
      errors++;
    }
    
    // Pequeno delay para não sobrecarregar
    await new Promise(r => setTimeout(r, 100));
  }
  
  return { success, errors, total: affectedUsers.length };
};
